import { useLingui } from '@lingui/react'
import {
  type NowMerchantExtended,
  type NowSlotExtended,
} from '@redteclab/api/clients/bully'
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { IS_JEST, PAGE_TYPE_PRODUCT } from 'shared/consts'
import {
  NOW_INVALID_PLZ,
  NOW_PDP_SHIPPING_MODAL_SUBMIT,
  NOW_VALID_PLZ,
} from 'shared/experiments/consts'
import { activateExperiment } from 'shared/experiments/utils/experiments'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import logger from 'shared/services/logger'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import {
  type IState,
  type NowDataState,
  type NowDataStateSlot,
} from 'types/redux'
import Modal from 'views/components/molecules/Modal'
import { validateZipCode } from 'views/components/organisms/NowShippingModal/NowShippingModalContent'
import NowZipForm, {
  type INowDeliveryBoxData,
} from 'views/components/organisms/NowShippingModal/NowShippingModalContent/NowZipForm'
import {
  MINIMUM_VALUE_NOT_REACHED,
  PRODUCTS_NOT_AVAILABLE,
} from 'views/consts/notEligibleReasons'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'
import { getExperimentNameForPageType } from 'views/utils/nowGlobalBar'

interface IProductNowShippingModal {
  id: string
  open: boolean
  setModalOpen: Dispatch<SetStateAction<boolean>>
  upid: string
  price: number
  nowEligible: boolean
  setNowEligible: Dispatch<SetStateAction<boolean>>
}

export const trackEligibleProduct = (nowEligible: boolean) => {
  return nowEligible ? NOW_VALID_PLZ : NOW_INVALID_PLZ
}

const ProductNowShippingModal: (
  props: IProductNowShippingModal
) => ReactElement = ({
  open,
  id,
  setModalOpen,
  upid,
  price,
  nowEligible,
  setNowEligible,
}) => {
  const { i18n } = useLingui()
  const [notEligibleReasonCode, setNotEligibleReasonCode] = useState<
    number | undefined | null
  >(0)
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)

  const [zipCode, setZipCode] = useState(nowData?.zipCode)
  const [nowDeliveryBoxData, setNowDeliveryBoxData] =
    useState<INowDeliveryBoxData | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const fetchNowData: (zipToUse: string) => Promise<void> = useCallback(
    async zipToUse => {
      try {
        if (zipToUse) {
          const { nowService } = buildClientAPI()
          const response = await nowService.fetchBasicData({
            zipCode: zipToUse,
            products: [
              {
                upid,
                quantity: 1,
                priceCentsGross: price * 100,
                nowProduct: 0,
              },
            ],
          })
          let notEligibleReason: number | undefined | null =
            response.notEligibleReason

          if (notEligibleReason === MINIMUM_VALUE_NOT_REACHED) {
            notEligibleReason = undefined
          }

          setNotEligibleReasonCode(notEligibleReason)
          const nowEligibleProduct: boolean =
            response.nowEligible ||
            response.notEligibleReason === MINIMUM_VALUE_NOT_REACHED
          setNowEligible(nowEligibleProduct)

          trackOptimizelyEvent(trackEligibleProduct(nowEligibleProduct))

          let slots: NowSlotExtended[] = []
          if (response?.slots?.length) {
            const slot = response.slots[0]
            slots = response.slots.filter(
              item => item.deliveryDay === slot.deliveryDay
            )
          }

          setNowDeliveryBoxData({
            slots: slots as NowDataStateSlot[],
            merchant: { ...response.merchant } as NowMerchantExtended,
            centsDifferenceMinimumOrderValue:
              response.centsDifferenceMinimumOrderValue,
          })
          dispatch(
            updateNowDataSuccessAction({
              ...response,
              city: nowData?.city,
              zipCode: zipToUse,
              refreshNowData: false,
            })
          )
          setZipCode(zipToUse)
        }
      } catch (error) {
        logger.error(error)
      }
    },
    [dispatch, nowData?.city, price, setNowEligible, upid]
  )

  useEffect(() => {
    const experiment = getExperimentNameForPageType(PAGE_TYPE_PRODUCT)
    if (
      experiment &&
      // avoid client call in Jest as no mock available
      !IS_JEST
    ) {
      void activateExperiment(experiment)
    }
  }, [])

  const validateZipAndFetchNowData = useCallback(
    async (zipToUse?: string) => {
      if (!zipToUse) {
        dispatch(updateNowDataSuccessAction({}))
        return
      }

      if (validateZipCode(zipToUse, i18n, setErrorMessage, tenant)) {
        if (zipToUse) {
          setIsLoading(true)
        }

        try {
          await fetchNowData(zipToUse)
        } catch (e) {
          setIsLoading(false)

          setNotEligibleReasonCode(PRODUCTS_NOT_AVAILABLE)
          setNowEligible(false)
        }
        trackOptimizelyEvent(NOW_PDP_SHIPPING_MODAL_SUBMIT)
      }
    },
    [dispatch, fetchNowData, i18n, setNowEligible, tenant]
  )

  useEffect(() => {
    if (nowData?.openZipLabelModal) {
      setModalOpen(true)

      dispatch(
        updateNowDataSuccessAction({
          ...nowData,
          openZipLabelModal: false,
        })
      )
    }
  }, [dispatch, nowData, nowData?.openZipLabelModal, setModalOpen])

  useEffect(() => {
    setIsLoading(false)
    if (!nowData) {
      return
    }

    if (nowData?.refreshNowData) {
      dispatch(
        updateNowDataSuccessAction({
          ...nowData,
          openZipLabelModal: false,
          refreshNowData: false,
        })
      )
      setZipCode(nowData?.zipCode)
      void validateZipAndFetchNowData(nowData?.zipCode)
    }

    if (!nowData?.zipCode) {
      setNotEligibleReasonCode(undefined)
      setNowEligible(false)
      setErrorMessage('')
      setZipCode('')
    }
  }, [
    dispatch,
    validateZipAndFetchNowData,
    nowData,
    setModalOpen,
    setNowEligible,
  ])

  const handleSubmit = useCallback(
    (zipToUse: string) => {
      dispatch(
        updateNowDataSuccessAction({
          ...nowData,
          zipCode: zipToUse,
          refreshNowData: true,
        })
      )
    },
    [dispatch, nowData]
  )
  const onModalHide = useCallback(() => {
    setModalOpen(false)
  }, [setModalOpen])
  return (
    <Modal
      id={id}
      className="o-ProductNowShippingModal"
      title={i18n._('general.label.nowGlobalBar.chooseLocation')}
      size="medium"
      light
      open={open}
      isClientSideControlled
      onHideModal={onModalHide}
    >
      <NowZipForm
        nowEligible={nowEligible}
        zipCode={zipCode}
        setZipCode={setZipCode}
        setErrorMessage={setErrorMessage}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        nowDeliveryBoxData={nowDeliveryBoxData}
        notEligibleReasonCode={notEligibleReasonCode}
        errorMessage={errorMessage}
      />
    </Modal>
  )
}

export default ProductNowShippingModal
